/**
 * Lazy registry for per-track exam question banks.
 *
 * Why this exists: src/data/examQuestions.ts is ~340KB and gets bundled into
 * the `lesson-data` chunk. Previously courses.ts statically imported every
 * track's question array, which dragged the entire 226KB chunk into the
 * homepage's critical path. Now courses.ts only carries question *counts*,
 * and the actual questions are fetched on demand by ExamPage / TrackPage.
 */
import type { ExamQuestion } from "./courses";

type Loader = () => Promise<ExamQuestion[]>;

// Single dynamic import — Vite splits this whole module into the lesson-data
// chunk; resolving any of these properties only triggers that chunk fetch
// when called (i.e. when a user actually opens an exam or results page).
const loadAll = () => import("./examQuestions");

const loaders: Record<string, Loader> = {
  foundations: () => loadAll().then((m) => m.foundationsExamQuestions),
  "blue-team": () => loadAll().then((m) => m.blueTeamExamQuestions),
  "red-team": () => loadAll().then((m) => m.redTeamExamQuestions),
  specialist: () => loadAll().then((m) => m.specialistExamQuestions),
  "oscp-prep": () => loadAll().then((m) => m.oscpExamQuestions),
  "bug-bounty": () => loadAll().then((m) => m.bugBountyExamQuestions),
  grc: () => loadAll().then((m) => m.grcExamQuestions),
  "mobile-security": () => loadAll().then((m) => m.mobileSecurityExamQuestions),
  "soc-analyst": () => loadAll().then((m) => m.socAnalystExamQuestions),
  "kali-labs": () => loadAll().then((m) => m.kaliLabsExamQuestions),
  "cloud-security": () => loadAll().then((m) => m.cloudSecurityExamQuestions),
  "malware-analysis": () => loadAll().then((m) => m.malwareAnalysisExamQuestions),
  "red-team-ops": () => loadAll().then((m) => m.redTeamOpsExamQuestions),
  "ai-llm-security": () => loadAll().then((m) => m.aisecExamQuestions),
  "web3-security": () => loadAll().then((m) => m.web3ExamQuestions),
  "purple-team": () => loadAll().then((m) => m.purpleExamQuestions),
  "threat-hunting-adv": () => loadAll().then((m) => m.huntExamQuestions),
  "network-security-eng": () => loadAll().then((m) => m.netsecExamQuestions),
  "career-skills": () => loadAll().then((m) => m.careerExamQuestions),
};

export async function loadExamQuestions(trackId: string): Promise<ExamQuestion[]> {
  const loader = loaders[trackId];
  if (!loader) return [];
  return loader();
}
